import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { TenantSwitcher } from "./tenant-switcher";
import { CommandPalette } from "./command-palette";

function useIsMac() {
  const [isMac, setIsMac] = useState(false);
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const platform = navigator.platform || "";
    const ua = navigator.userAgent || "";
    setIsMac(/Mac|iPhone|iPad|iPod/.test(platform) || /Mac OS X/.test(ua));
  }, []);
  return isMac;
}

export function AdminTopBar() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteInitialQuery, setPaletteInitialQuery] = useState("");
  const isMac = useIsMac();
  const shortcutLabel = isMac ? "⌘K" : "Ctrl K";

  // Global ⌘K / Ctrl+K shortcut. Skip if user is typing inside another input
  // already, unless the modifier is pressed — that's always an intentional
  // open.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteInitialQuery("");
        setPaletteOpen(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  return (
    <>
      <div
        role="banner"
        className={cn(
          "sticky top-0 z-30 flex h-11 shrink-0 items-center gap-2 border-b border-border bg-background px-3 sm:px-4",
        )}
      >
        <span
          className="shrink-0 rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-accent"
          aria-label="MSP mode"
        >
          MSP MODE
        </span>
        <span className="hidden shrink-0 text-[11px] text-text-muted sm:inline">
          Jump to tenant:
        </span>
        <TenantSwitcher />

        <div className="ml-2 flex min-w-0 flex-1 items-center">
          {/* Wide: full search field */}
          <button
            type="button"
            onClick={() => {
              setPaletteInitialQuery("");
              setPaletteOpen(true);
            }}
            aria-label="Open global search"
            className={cn(
              "hidden sm:flex w-full max-w-xl items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-1 text-left text-xs text-text-muted transition-colors",
              "hover:bg-surface-hover hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
            )}
          >
            <Search size={13} />
            <span className="min-w-0 flex-1 truncate">Search tenants, agents, skills…</span>
            <kbd className="shrink-0 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] text-text-muted">
              {shortcutLabel}
            </kbd>
          </button>
          {/* Narrow: icon-only trigger */}
          <button
            type="button"
            onClick={() => {
              setPaletteInitialQuery("");
              setPaletteOpen(true);
            }}
            aria-label="Open global search"
            className={cn(
              "flex sm:hidden ml-auto items-center justify-center rounded-md border border-border bg-surface p-1.5 text-text-muted transition-colors",
              "hover:bg-surface-hover hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
            )}
          >
            <Search size={14} />
          </button>
        </div>
      </div>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        initialQuery={paletteInitialQuery}
      />
    </>
  );
}
